// ui/focus.js — 모달 안에서 **키보드가 갇히는 규칙**을 계산하는 자리.
//
// 이 파일이 DOM 을 만지지 않는 것이 요점이다. 여기 있는 것은 전부 순수 함수라서,
// 「마지막에서 Tab 하면 처음으로 돌아온다」 같은 문장을 브라우저 없이 값으로 확인할 수
// 있다. DOM 을 읽는 것은 `focusableWithin` 하나뿐이고, 그것도 `querySelectorAll` 의
// 결과를 같은 순수 필터에 넘긴다 — 실제 화면과 테스트가 같은 판정을 쓴다.
//
// 캐시하지 않는 이유: Preact 는 다시 그릴 때 노드를 갈아 끼운다. 열 때 한 번 모아 둔
// 목록은 한 번의 리렌더로 죽은 노드를 가리키게 되고, 그때부터 Tab 은 조용히 밖으로 샌다.
// 그래서 **누를 때마다 다시 모은다.**

/** 브라우저가 기본 tab 순서에 넣는 것들. `[tabindex]` 는 음수 제외를 아래 필터가 한다. */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'details > summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
  '[tabindex]',
].join(', ');

/**
 * 실제로 초점을 받을 수 있는 것만 남긴다.
 *
 * 요소가 아니라 **모양**만 본다(`disabled` · `hidden` · `tabIndex` · `aria-hidden` ·
 * 조상의 `inert`). 그래서 테스트가 가벼운 대역으로 같은 규칙을 확인할 수 있다.
 */
export function focusableCandidates(elements) {
  return [...(elements ?? [])].filter((element) => {
    if (!element) return false;
    if (element.disabled === true) return false;
    if (element.hidden === true) return false;
    if (typeof element.tabIndex === 'number' && element.tabIndex < 0) return false;
    if (element.getAttribute?.('aria-hidden') === 'true') return false;
    if (element.getAttribute?.('tabindex') === '-1') return false;
    // `inert` 안의 것은 초점을 받지 않는다 — 배경을 잠근 뒤에도 목록에 남으면
    // Tab 이 잠긴 배경으로 들어간다.
    if (element.closest?.('[inert]')) return false;
    if (insideClosedDetails(element)) return false;
    if (!isRendered(element)) return false;
    return true;
  });
}

/**
 * 접힌 `<details>` 안인가.
 *
 * 반환 대화상자의 「덧붙일 것」이 정확히 이 모양이다. 접혀 있으면 안의 칸들은 **보이지
 * 않는데 DOM 에는 있다.** 그걸 초점 후보로 세면 Tab 이 보이지 않는 자리로 들어가고,
 * 사람은 초점이 사라졌다고 느낀다(그게 갇힘의 반대다).
 *
 * 다만 그 `<details>` 의 `<summary>` 는 접혀 있어도 보이고 눌러야 하는 것이다 — 남긴다.
 */
function insideClosedDetails(element) {
  const closed = element.closest?.('details:not([open])');
  if (!closed) return false;
  const tag = String(element.tagName ?? '').toLowerCase();
  return !(tag === 'summary' && element.parentElement === closed);
}

/**
 * 지금 화면에 **그려져 있나.**
 *
 * `hidden` 속성만 보면 `display:none` 인 조상, 0×0 으로 접힌 자리, `visibility:hidden` 은
 * 그대로 통과한다. 실제 화면에서는 그 셋이 훨씬 흔하다(반응형에서 숨긴 「목록으로」 버튼이
 * 그렇다). 잴 수 없는 환경(테스트 대역)에서는 **판단하지 않는다** — 없는 근거로 후보를
 * 지우면, 브라우저에서는 멀쩡한 컨트롤이 목록에서 조용히 빠진다.
 */
function isRendered(element) {
  if (typeof element.getClientRects === 'function' && element.getClientRects().length === 0) return false;
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return true;
  if (element.nodeType !== 1) return true;
  const style = window.getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

/** 대화상자 안에서 지금 초점을 받을 수 있는 것들. 순서는 DOM 순서다. */
export function focusableWithin(root) {
  if (!root?.querySelectorAll) return [];
  return focusableCandidates(root.querySelectorAll(FOCUSABLE_SELECTOR));
}

/**
 * 다음 초점의 자리. 끝에서 넘어가면 **반대쪽 끝으로 돌아온다**(그게 갇힘이다).
 *
 * @param {number} length  초점 후보 수
 * @param {number} current 지금 초점의 자리. 목록 밖이면 -1
 * @param {boolean} backwards Shift+Tab
 * @returns {number} -1 이면 초점을 줄 곳이 없다
 */
export function nextFocusIndex(length, current, backwards) {
  if (length <= 0) return -1;
  if (current < 0 || current >= length) return backwards ? length - 1 : 0;
  const next = backwards ? current - 1 : current + 1;
  if (next < 0) return length - 1;
  if (next >= length) return 0;
  return next;
}

/**
 * Tab 한 번에 **우리가 직접 초점을 옮겨야 하는가.**
 *
 * 가운데에서의 Tab 은 브라우저가 이미 맞게 처리한다 — 거기까지 가로채면 IME·스크롤
 * 같은 기본 동작이 함께 사라진다. 그래서 가장자리(마지막→처음, 처음→마지막)와
 * 「지금 초점이 대화상자 밖에 있다」 두 경우에만 값을 돌려준다.
 *
 * @returns 옮길 요소, 또는 null(= 브라우저에 맡긴다)
 */
export function trapTarget(elements, active, backwards = false) {
  const list = [...(elements ?? [])];
  if (list.length === 0) return null;
  const current = list.indexOf(active);
  if (current === -1) return backwards ? list[list.length - 1] : list[0];
  const atEdge = backwards ? current === 0 : current === list.length - 1;
  if (!atEdge) return null;
  return list[nextFocusIndex(list.length, current, backwards)];
}

/**
 * 열었을 때 처음 초점이 갈 자리.
 *
 * 호출자가 지목한 것이 있으면 그것, 없으면 첫 후보, 그것도 없으면 대화상자 자신이다
 * (`tabindex="-1"` 이 붙어 있다). **body 로 떨어뜨리지 않는다** — 거기서 Tab 하면
 * 잠긴 배경으로 들어간다.
 */
export function initialFocus(elements, preferred = null, fallback = null) {
  if (preferred && focusableCandidates([preferred]).length === 1) return preferred;
  const list = [...(elements ?? [])];
  return list[0] ?? fallback ?? null;
}
